import axios from "axios";
import { atom, useAtom } from "jotai";
import { SetStateAction } from "jotai/core/types";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useErrorHandler } from "react-error-boundary";

import { updateFormioSubmission } from "../shared/api/yenta-api";

import {
  WORD_SEARCH_DEFAULT_GRID_CELL_HEIGHT,
  WORD_SEARCH_DEFAULT_GRID_CELL_WIDTH,
} from "./word-search.constants";
import {
  CplStatus,
  Word,
  WordFieldsMap,
  WordSearchApiData,
  WordSearchField as Field,
  WordSearchFieldStatus,
  WordSearchGameState,
  WordSearchGameStatus,
} from "./word-search.types";

const { wordfind } = require("./wordfind");

export const wordSearchRoundAtom = atom<
  WordSearchApiData,
  SetStateAction<WordSearchApiData>
>(
  async () => {
    try {
      const { data } = await axios.get<WordSearchApiData>(
        `${
          window.FKL.yentaServicesApiUrl
        }/word-search/${window.FKL.wordSearchSlug.trim()}`
      );

      return data;
    } catch (error) {
      throw new Error(error.response?.data?.message || error.message);
    }
  },
  (get, set, update) => {
    const newValue =
      typeof update === "function" ? update(get(wordSearchRoundAtom)) : update;
    set(wordSearchRoundAtom, newValue);
  }
);

export function initFields(
  words: Word[],
  width = WORD_SEARCH_DEFAULT_GRID_CELL_WIDTH,
  height = WORD_SEARCH_DEFAULT_GRID_CELL_HEIGHT
): { fields: Field[]; fieldsPerWords: WordFieldsMap } {
  const { puzzle, fieldsPerWords } = wordfind.newPuzzle(
    words.map((word) => word.content),
    {
      height,
      width,
      preferOverlap: false,
      orientations: ["horizontal", "vertical", "diagonal", "diagonalBack"],
    }
  );

  const fields = puzzle
    .reduce((acc: Field[], row: Field[]) => [...acc, ...row], [])
    .map((field: Field, index: number) => ({
      ...field,
      index,
      status: WordSearchFieldStatus.NONE,
    }));

  return { fields, fieldsPerWords };
}

export function toggleFieldState(
  prevState: WordSearchGameState,
  field: Field
): WordSearchGameState {
  const newFields = [...prevState.fields];
  const fieldToChange = newFields[field.index];
  newFields[field.index] = {
    ...fieldToChange,
    status:
      fieldToChange.status === WordSearchFieldStatus.NONE
        ? WordSearchFieldStatus.SELECTED
        : WordSearchFieldStatus.NONE,
  };

  return {
    ...prevState,
    status:
      prevState.status === WordSearchGameStatus.IDLE
        ? WordSearchGameStatus.STARTED
        : prevState.status,
    fields: newFields,
  };
}

export function completeWords(
  prevState: WordSearchGameState,
  words: Word[]
): WordSearchGameState {
  const foundWords = new Set(
    words.filter((word) => word.found).map((word) => word.content)
  );
  const fields = prevState.fields.map((field: Field) => {
    if (field.words.find((word) => foundWords.has(word))) {
      return {
        ...field,
        status: WordSearchFieldStatus.FOUND,
      };
    }
    return field;
  });

  return {
    ...prevState,
    fields,
    words,
    status:
      prevState.status === WordSearchGameStatus.IDLE
        ? WordSearchGameStatus.STARTED
        : prevState.status,
  };
}

export function selectWordField(
  prevState: WordSearchGameState,
  field: Field
): WordSearchGameState {
  const currentWords = prevState.words.filter((word) =>
    field.words.includes(word.content)
  );
  if (currentWords.length === 0) {
    return prevState;
  }

  const words = prevState.words.map((word) => {
    const currentWord = currentWords.find((w) => w.content === word.content);
    if (!currentWord) {
      return word;
    }

    const wordFields = prevState.fieldsPerWords[currentWord.content];
    const firstLetterIndex = wordFields[0];
    const lastLetterIndex = wordFields[wordFields.length - 1];
    const isFirstLetterSelected =
      prevState.fields[firstLetterIndex].status ===
      WordSearchFieldStatus.SELECTED;
    const isLastLetterSelected = field.index === lastLetterIndex;
    if (isFirstLetterSelected && isLastLetterSelected) {
      return {
        ...word,
        lettersSelected: word.content.length,
        found: true,
      };
    }

    const lettersFound = currentWord.lettersSelected + 1;
    const wordCompleted = lettersFound === currentWord.content.length;

    return {
      ...word,
      lettersSelected: lettersFound,
      found: wordCompleted,
    };
  });

  if (
    words.filter((word) => field.words.includes(word.content) && word.found)
      .length > 0
  ) {
    return completeWords(prevState, words);
  }

  const newFields = [...prevState.fields];
  const fieldToChange = newFields[field.index];
  newFields[field.index] = {
    ...fieldToChange,
    status: WordSearchFieldStatus.SELECTED,
  };

  return {
    ...prevState,
    words,
    fields: newFields,
    status:
      prevState.status === WordSearchGameStatus.IDLE
        ? WordSearchGameStatus.STARTED
        : prevState.status,
  };
}

export function deselectWordField(
  prevState: WordSearchGameState,
  field: Field
): WordSearchGameState {
  const currentWords = prevState.words.filter((word) =>
    field.words.includes(word.content)
  );
  if (currentWords.length === 0) {
    return prevState;
  }

  const words = prevState.words.map((word) => {
    const currentWord = currentWords.find((w) => w.content === word.content);
    if (currentWord) {
      return {
        ...word,
        lettersSelected: word.lettersSelected - 1,
      };
    }
    return word;
  });

  const newFields = [...prevState.fields];
  const fieldToChange = newFields[field.index];
  newFields[field.index] = {
    ...fieldToChange,
    status: WordSearchFieldStatus.NONE,
  };

  return {
    ...prevState,
    words,
    fields: newFields,
  };
}

function getLastRoundIndex(data: WordSearchApiData): number {
  const submission = window.theme.Sweepstake.prototype.retrieveSubmission();
  const lastRoundId = submission?.data.lastWordSearchRoundId;

  return lastRoundId
    ? data.rounds.findIndex((round) => round.id === lastRoundId)
    : -1;
}

const defaultGameState: WordSearchGameState = {
  words: [],
  fieldsPerWords: {},
  fields: [],
  status: WordSearchGameStatus.IDLE,
  cplStatus: CplStatus.NONE,
  roundIndex: null,
};

export function useWordSearchState() {
  const [data] = useAtom(wordSearchRoundAtom);
  const handleWidgetError = useErrorHandler();
  const [gameState, setGameState] = useState<WordSearchGameState>(
    defaultGameState
  );

  const handleNextRound = useCallback(() => {
    if (!data) {
      return;
    }
    const lastRoundIndex = getLastRoundIndex(data);
    const nextRoundIndex =
      lastRoundIndex + 1 >= data.rounds.length ? 0 : lastRoundIndex + 1;

    setGameState({
      ...defaultGameState,
      roundIndex: nextRoundIndex,
    });
  }, [data, setGameState]);

  useEffect(() => {
    handleNextRound();
  }, [handleNextRound]);

  const round = useMemo(() => {
    return data && gameState.roundIndex !== null
      ? data.rounds[gameState.roundIndex]
      : null;
  }, [data, gameState.roundIndex]);

  const wordsFound = useMemo(() => {
    return gameState.words.filter((word) => word.found).length;
  }, [gameState.words]);

  const handleCplResponse = useCallback(
    (isAccepted: boolean) => {
      setGameState((prevState) => ({
        ...prevState,
        cplStatus: isAccepted ? CplStatus.ACCEPTED : CplStatus.DENIED,
      }));
    },
    [setGameState]
  );

  const handleTimeout = useCallback(() => {
    setGameState((prev) => ({
      ...prev,
      status: WordSearchGameStatus.LOST,
    }));
  }, []);

  const handleClick = useCallback(
    (field: Field) => {
      if (field.status === WordSearchFieldStatus.FOUND) {
        return;
      }

      if (field.words.length === 0) {
        return setGameState((prevState) => toggleFieldState(prevState, field));
      }

      if (field.status === WordSearchFieldStatus.NONE) {
        return setGameState((prevState) => selectWordField(prevState, field));
      }

      if (field.status === WordSearchFieldStatus.SELECTED) {
        setGameState((prevState) => deselectWordField(prevState, field));
      }
    },
    [setGameState]
  );

  useEffect(() => {
    if (!round || gameState.status !== WordSearchGameStatus.IDLE) {
      return;
    }

    const words: Word[] = round.products.map((product) => ({
      content: product.name,
      lettersSelected: 0,
      found: false,
    }));
    const { fields, fieldsPerWords } = initFields(words);
    setGameState((prev) => ({
      ...prev,
      fields,
      words,
      fieldsPerWords,
    }));
  }, [round, gameState.status]);

  useEffect(() => {
    if (
      gameState.status === WordSearchGameStatus.STARTED &&
      wordsFound === gameState.words.length
    ) {
      setGameState((prev) => ({
        ...prev,
        status: WordSearchGameStatus.WON,
      }));
    }
  }, [wordsFound, gameState.words, gameState.status]);

  const initialWordsFound = useMemo(() => {
    const submission = window.theme.Sweepstake.prototype.retrieveSubmission();
    return (round && Number(submission?.data.wordsFound)) || 0;
  }, [round]);

  const allTimeEntries = useMemo(() => {
    const submission = window.theme.Sweepstake.prototype.retrieveSubmission();
    return (round && Number(submission?.data.quizExtraEntries)) || 0;
  }, [round]);

  useEffect(() => {
    const submission = window.theme.Sweepstake.prototype.retrieveSubmission();
    if (!submission) {
      return;
    }

    const sweepFormUrl =
      document
        .querySelector("#FormSweepstake")
        ?.getAttribute("data-form-path-url") || "";
    const gameHasEnded = [
      WordSearchGameStatus.WON,
      WordSearchGameStatus.LOST,
    ].includes(gameState.status);
    const quizAcceptedCpls = Array.from(
      new Set(
        [
          ...((submission.data.quizAcceptedCpls as string) || "").split(","),
          gameState.cplStatus === CplStatus.ACCEPTED && round?.cpl.name,
        ].filter(Boolean)
      )
    ).join(",");
    const patchData = {
      ...submission.data,
      quizAcceptedCpls,
      quizExtraEntries: allTimeEntries + wordsFound,
      wordsFound: initialWordsFound + wordsFound,
      ...(gameHasEnded ? { lastWordSearchRoundId: round?.id } : {}),
    };

    updateFormioSubmission(
      sweepFormUrl,
      {
        ...submission,
        data: patchData,
      },
      handleWidgetError
    )
      .then((submission: any) => {
        return window.theme.Sweepstake.prototype.storeSubmission(submission);
      })
      .then(() => {
        if (gameState.cplStatus !== CplStatus.NONE) {
          handleNextRound();
        }
      })
      .catch((error) => console.log(error));
  }, [
    gameState.status,
    gameState.cplStatus,
    wordsFound,
    round,
    initialWordsFound,
    allTimeEntries,
    handleNextRound,
    handleWidgetError,
  ]);

  return {
    data,
    round,
    gameState,
    handleClick,
    handleTimeout,
    handleCplResponse,
    wordsFound,
    allTimeEntries,
  };
}
